/**
 * Groups delivery fees by supplier and keeps the highest fee (zero or positive) and most negative discount per group.
 *
 * Note: a fee of exactly 0 is treated as a valid fee, not "no fee". This lets a custom delivery
 * fee of $0 override a supplier's default rate (CPC-775). A 0 never displaces a higher positive
 * fee, since `0 > existing` is false.
 *
 * @param {Array} items - Array of items with delivery fee information
 * @param {Function} getKey - Function to extract the grouping key from an item
 * @param {Function} getAmount - Function to extract the amount from an item
 * @param {Function} createFeeObject - Function to create the fee object to store in the group
 * @returns {Object} Object with groupedBySupplier structure
 */
export function groupDeliveryFeesBySupplier(items, getKey, getAmount, createFeeObject) {
  const groupedBySupplier = {};

  items.forEach((item) => {
    const key = getKey(item);
    
    // Skip if we can't identify the supplier
    if (!key) return;

    const amount = getAmount(item);

    // Initialize group if it doesn't exist
    if (!groupedBySupplier[key]) {
      groupedBySupplier[key] = {
        positive: null,
        negative: null,
      };
    }

    const group = groupedBySupplier[key];

    // For non-negative amounts (delivery fees, incl. an explicit $0 custom fee), keep the highest fee
    const isPositiveAmount = amount >= 0;
    const shouldUpdatePositive = isPositiveAmount && (!group.positive || amount > group.positive.amount);
    
    // For negative amounts (discounts), keep the most negative (largest discount)
    const isNegativeAmount = amount < 0;
    const shouldUpdateNegative = isNegativeAmount && (!group.negative || amount < group.negative.amount);

    if (shouldUpdatePositive) {
      group.positive = createFeeObject(item, amount);
    } else if (shouldUpdateNegative) {
      group.negative = createFeeObject(item, amount);
    }
  });

  return groupedBySupplier;
}

/**
 * A cart item's stacked delivery fee (e.g. cold chain): its delivery fee line
 * flagged isStackedDeliveryFee, carried on top of freight. A supplier has one
 * stacked fee, so the highest line is taken, matching buildDeliveryFeeLines.
 *
 * @param {Object} item - Cart item with deliveryFees lines
 * @returns {number} Amount in cents
 */
export function stackedDeliveryFee(item) {
  return (item.deliveryFees || [])
    .filter((fee) => fee.isStackedDeliveryFee)
    .reduce((highest, fee) => Math.max(highest, Number(fee.amount || 0)), 0);
}

/**
 * A cart item's freight only: its delivery total without the stacked fee.
 *
 * @param {Object} item - Cart item with deliveryFeeTotal and deliveryFees lines
 * @returns {number} Amount in cents
 */
export function freightDeliveryFee(item) {
  return Number(item.deliveryFeeTotal || 0) - stackedDeliveryFee(item);
}

/**
 * The cart's delivery, matching what the API charges: per supplier group, the
 * highest freight (a custom delivery fee competes with freight only), plus the
 * group's stacked delivery fee once.
 *
 * @param {Array} items - Cart items
 * @returns {{total: number, appliedCustomDeliveryFee: Object}} Total in cents, and the
 *   custom fee that won per supplier id, for checkouts that list fees per line
 */
export function calculateCartDelivery(items) {
  let total = 0;
  const appliedCustomDeliveryFee = {};

  // Group by sageSupplierId (fallback to managedBy) and select highest positive fee + most-negative discount
  const getKey = (item) => {
    const inventory = item.inventory || {};
    return (item.product && item.product.supplier && item.product.supplier.sageSupplierId) || inventory.managedBy;
  };

  const getAmount = (item) => Number((item.isCustomDeliveryFee ? item.customDeliveryFee : freightDeliveryFee(item)) || 0);

  const createFeeObject = (item, amount) => {
    const inventory = item.inventory || {};
    return {
      amount,
      supplierId: inventory.managedBy,
      isCustom: item.isCustomDeliveryFee,
    };
  };

  const groupedBySupplier = groupDeliveryFeesBySupplier(items, getKey, getAmount, createFeeObject);

  Object.values(groupedBySupplier).forEach(({ positive, negative }) => {
    if (positive) {
      total += positive.amount;
      if (positive.isCustom) {
        appliedCustomDeliveryFee[positive.supplierId] = positive.amount;
      }
    }
    if (negative) {
      total += negative.amount;
      if (negative.isCustom) {
        appliedCustomDeliveryFee[negative.supplierId] = negative.amount;
      }
    }
  });

  // The stacked fee is charged once per supplier group, on top of freight.
  const stackedBySupplier = {};
  items.forEach((item) => {
    const key = getKey(item);
    if (!key) return;
    stackedBySupplier[key] = Math.max(stackedBySupplier[key] || 0, stackedDeliveryFee(item));
  });
  Object.values(stackedBySupplier).forEach((amount) => {
    total += amount;
  });

  return {
    total,
    appliedCustomDeliveryFee,
  };
}

/**
 * The cart's delivery fee lines for display: per supplier group, the highest
 * freight line and largest discount line, with a winning custom delivery fee
 * replacing freight, plus the stacked delivery fee line once on its own.
 *
 * @param {Array} items - Cart items with deliveryFees lines
 * @param {Object} appliedCustomDeliveryFee - Winning custom fee per supplier id, from calculateCartDelivery
 * @returns {Array} Delivery fee lines; their amounts add up to calculateCartDelivery's total
 */
export function buildDeliveryFeeLines(items, appliedCustomDeliveryFee) {
  const groupedBySupplier = {};

  items.forEach((val) => {
    const deliveries = (val.deliveryFees || []).map((i) => {
      const fee = { ...i };
      // A custom delivery fee replaces the freight, never the stacked fee.
      const overrideAmount = fee.isStackedDeliveryFee ? undefined : appliedCustomDeliveryFee[i.supplierId];
      if (typeof overrideAmount === 'number') {
        fee.amount = overrideAmount;
      }
      return fee;
    });

    // A stacked delivery fee is grouped on its own so it adds to the freight instead of competing with it.
    const getKey = (fee) => `${fee.sageSupplierId || fee.supplierId}${fee.isStackedDeliveryFee ? ':stacked' : ''}`;
    const getAmount = (fee) => Number(fee.amount || 0);
    const createFeeObject = (fee, amount) => ({ ...fee, amount });

    const feeGroupedBySupplier = groupDeliveryFeesBySupplier(deliveries, getKey, getAmount, createFeeObject);

    // Merge into main groupedBySupplier object
    Object.keys(feeGroupedBySupplier).forEach((key) => {
      if (!groupedBySupplier[key]) {
        groupedBySupplier[key] = {
          positive: null,
          negative: null,
        };
      }
      const group = groupedBySupplier[key];
      const feeGroup = feeGroupedBySupplier[key];

      // Update with highest positive and most negative from this item's fees
      if (feeGroup.positive && (!group.positive || feeGroup.positive.amount > group.positive.amount)) {
        group.positive = feeGroup.positive;
      }
      if (feeGroup.negative && (!group.negative || feeGroup.negative.amount < group.negative.amount)) {
        group.negative = feeGroup.negative;
      }
    });
  });

  const lines = [];
  Object.values(groupedBySupplier).forEach(({ positive, negative }) => {
    if (positive) lines.push(positive);
    if (negative) lines.push(negative);
  });
  return lines;
}
